import { createContext, useContext, useMemo } from 'react';
import useFetchCrmObjectTypeDefinition from 'marketing-platform-lib/hooks/useFetchCrmObjectTypeDefinition';
import useFetchCrmObject from 'framework-asset-preview-lib/internal/shared/hooks/useFetchCrmObject';
import useFetchCrmObjectProperties from 'framework-asset-preview-lib/internal/shared/hooks/useFetchCrmObjectProperties';
import { propertyGroupsToPropertyDefinitions } from 'framework-asset-preview-lib/internal/shared/utils/properties';
import useFetchNonCrmObject from 'framework-asset-preview-lib/internal/shared/hooks/useFetchNonCrmObject';
import { useAssetPreview } from './AssetPreviewProvider';
import { getPropertyValue } from 'marketing-platform-lib/utils/crmUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const DataContext = /*#__PURE__*/createContext(undefined);
export default function DataProvider({
  children,
  crmObject: crmObjectProp,
  propertyGroups: propertyGroupsProp
}) {
  const {
    objectId,
    objectType
  } = useAssetPreview();

  /**
   * CRM Object fetch
   */
  const {
    data: crmObjectFromFetch,
    error: errorCrmObjectFetch,
    loading: loadingCrmObject,
    refetch
  } = useFetchCrmObject({
    objectId,
    objectType,
    skip: !!crmObjectProp
  });
  const {
    data: nonCrmObject,
    error: errorNonCrmObject,
    loading: loadingNonCrmObject
  } = useFetchNonCrmObject(objectType, crmObjectProp || crmObjectFromFetch || undefined);

  /**
   * Properties fetch
   */
  const skipPropertiesFetch = useMemo(() => Boolean(propertyGroupsProp && propertyGroupsProp.length > 0), [propertyGroupsProp]);
  const {
    properties: propertiesFromFetch,
    propertyGroups: propertyGroupsFromFetch,
    error: errorProperties,
    loading: loadingProperties
  } = useFetchCrmObjectProperties(objectType, {
    skip: skipPropertiesFetch
  });
  const properties = useMemo(() => {
    if (skipPropertiesFetch && propertyGroupsProp) {
      return propertyGroupsToPropertyDefinitions(propertyGroupsProp);
    }
    return propertiesFromFetch;
  }, [propertiesFromFetch, propertyGroupsProp, skipPropertiesFetch]);
  const {
    data: typeDefinition,
    error: errorTypeDefinition,
    loading: loadingTypeDefinition
  } = useFetchCrmObjectTypeDefinition(objectType);

  /**
   * Loading state
   */
  const isLoading = useMemo(() => {
    if (loadingCrmObject || loadingProperties || loadingTypeDefinition || loadingNonCrmObject) {
      return true;
    }

    /**
     * Panel is already open but for a different object type
     */
    if (crmObjectFromFetch && crmObjectFromFetch.objectTypeId !== objectType) {
      return true;
    }

    /**
     * Panel is already open but for a different object type
     */
    if (crmObjectProp && crmObjectProp.objectTypeId !== objectType) {
      return true;
    }
    return false;
  }, [crmObjectFromFetch, crmObjectProp, loadingCrmObject, loadingNonCrmObject, loadingProperties, loadingTypeDefinition, objectType]);

  /**
   * Error state
   */
  const error = useMemo(() => {
    const crmObjectError = Boolean(errorCrmObjectFetch);
    const propertiesError = Boolean(errorProperties);
    const typeDefinitionError = Boolean(errorTypeDefinition);
    const nonCrmObjectError = Boolean(errorNonCrmObject);
    return {
      isError: crmObjectError || propertiesError || typeDefinitionError || nonCrmObjectError,
      isCrmObjectError: crmObjectError,
      isNonCrmObjectError: nonCrmObjectError,
      isPropertiesError: propertiesError,
      isTypeDefinitionError: typeDefinitionError
    };
  }, [errorCrmObjectFetch, errorNonCrmObject, errorProperties, errorTypeDefinition]);

  /**
   * When both `crmObjectProp` and `crmObjectFromFetch` are defined, we use the latest version of the two.
   * We take that based on the "lastModifiedPropertyName" property defined in  the type definition.
   * Otherwise we use `crmObjectProp` as the main source of truth.
   *
   * Scenarios for this to happen are:
   *
   * 1 - User use inline editing -> we need to use what's defined in `crmObjectFromFetch`
   * 2 - User edits a CRM object outside of preview-lib -> we need to use `crmObjectProp`
   * 3 - Anything in between -> we pick the object that has the most recent changes
   */
  const crmObject = useMemo(() => {
    if (crmObjectFromFetch && crmObjectProp && typeDefinition) {
      const {
        lastModifiedPropertyName
      } = typeDefinition;
      if (!lastModifiedPropertyName) {
        return crmObjectProp;
      }
      const lastModifiedDateFetch = parseInt(`${getPropertyValue(crmObjectFromFetch, lastModifiedPropertyName)}`, 10);
      const lastModifiedDateProp = parseInt(`${getPropertyValue(crmObjectProp, lastModifiedPropertyName)}`, 10);
      if (lastModifiedDateFetch >= lastModifiedDateProp) {
        return crmObjectFromFetch;
      }
      return crmObjectProp;
    }
    return crmObjectProp || crmObjectFromFetch || undefined;
  }, [crmObjectFromFetch, crmObjectProp, typeDefinition]);
  const propertyGroups = useMemo(() => propertyGroupsProp || propertyGroupsFromFetch, [propertyGroupsProp, propertyGroupsFromFetch]);
  const value = useMemo(() => ({
    crmObject,
    error,
    loading: isLoading,
    nonCrmObject,
    properties,
    propertyGroups,
    refetchCrmObject: refetch,
    typeDefinition
  }), [crmObject, error, isLoading, nonCrmObject, properties, propertyGroups, refetch, typeDefinition]);
  return /*#__PURE__*/_jsx(DataContext.Provider, {
    value: value,
    children: children
  });
}
export function usePreviewData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('usePreviewData must be used within a DataProvider');
  }
  return context;
}